import { Tooltip } from "@chakra-ui/react";
import { AlertErrorAlert } from "components/Alerts/AlertErrorAlert";
import SpinnerComponent from "components/Spinner/SpinnerComponent";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { GiWeightScale } from "react-icons/gi";
import { ButtonHeaderModule } from "styled/Buttons.styled";
import { IconGeneric } from "styled/Icons.styled";

export const GetWeihingButton = ({
  loading,
  setLoading,
  executeFunction
}) => {
  const [openAlert] = AlertErrorAlert();
  
  // Estados principales
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [currentScaleId, setCurrentScaleId] = useState(null);
  const [currentWeight, setCurrentWeight] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState('Desconectado');
  const [connectionClass, setConnectionClass] = useState('disconnected');
  const [lastWeightTime, setLastWeightTime] = useState(null);
  
  // Estados de notificación y log
  const [showNotificationState, setShowNotificationState] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [logEntries, setLogEntries] = useState([]);
  
  // Estado para impresión automática
  const [autoPrint, setAutoPrint] = useState(false);
  
  // Referencias para instancias únicas
  const pusherRef = useRef(null);
  const channelRef = useRef(null);
  const isMountedRef = useRef(true);
  const isInitializedRef = useRef(false);
  const currentScaleIdRef = useRef(null);

  // Memoizar funciones que no deben cambiar
  const addLogEntry = useMemo(() => (message, type = 'info') => {
    if (!isMountedRef.current) return;
    
    const timestamp = new Date().toLocaleTimeString();
    const newEntry = {
      id: Date.now(),
      timestamp,
      message,
      type
    };
    
    setLogEntries(prev => [...prev, newEntry]);
  }, []);

  const displayNotification = useMemo(() => (message, duration = 5000) => {
    if (!isMountedRef.current) return;
    
    setNotificationMessage(message);
    setShowNotificationState(true);
    
    setTimeout(() => {
      if (isMountedRef.current) {
        setShowNotificationState(false);
      }
    }, duration);
  }, []);

  const showWeightNotification = useMemo(() => (data) => {
    const message = `Nuevo peso: ${data.weight} kg`;
    displayNotification(message, 3000);
  }, [displayNotification]);

  // Inicializar Pusher una única vez
  const initPusher = useMemo(() => () => {
    // Verificar si ya está inicializado
    if (pusherRef.current || isInitializedRef.current) {
      addLogEntry('✅ Pusher ya está inicializado', 'info');
      return pusherRef.current;
    }

    if (!window.Pusher) {
      addLogEntry('Pusher JS no está disponible', 'error');
      return null;
    }

    try {
      addLogEntry('Inicializando Pusher por primera vez...', 'info');
      
      // Configuración optimizada de Pusher para evitar ERR_HTTP2_PING_FAILED
      // Priorizar WebSockets y deshabilitar XHR streaming problemático
      const pusher = new window.Pusher(process.env.REACT_APP_PUSHER_KEY || '476e354063cd02eb5f4f', {
        cluster: process.env.REACT_APP_PUSHER_CLUSTER || 'us2',
        encrypted: true,
        // Forzar uso de WebSockets primero, evitar XHR streaming
        enabledTransports: ['ws', 'wss'],
        // Deshabilitar transports problemáticos
        disabledTransports: ['xhr_streaming', 'xhr_polling'],
        // Configuración de reconexión
        enableStats: false,
        // Timeout para conexión
        activityTimeout: 30000,
        // Configuración de heartbeat
        pongTimeout: 6000,
        // Deshabilitar estadísticas que pueden causar problemas
        disableStats: true
      });

      pusherRef.current = pusher;
      isInitializedRef.current = true;

      // Configurar eventos de conexión con mejor manejo de errores
      pusher.connection.bind('connected', () => {
        addLogEntry('Conectado a Pusher (WebSocket)', 'success');
        setConnectionStatus('Conectado a Pusher');
        setConnectionClass('connected');
      });

      pusher.connection.bind('disconnected', () => {
        addLogEntry('Desconectado de Pusher', 'warning');
        setConnectionStatus('Desconectado de Pusher');
        setConnectionClass('disconnected');
        setIsSubscribed(false);
      });

      pusher.connection.bind('error', (error) => {
        // Filtrar errores de HTTP2_PING_FAILED que son warnings, no errores críticos
        if (error.error && error.error.data && error.error.data.code) {
          const errorCode = error.error.data.code;
          if (errorCode === 1006 || errorCode === 'ERR_HTTP2_PING_FAILED') {
            addLogEntry('Error de conexión HTTP/2 (intentando reconexión...)', 'warning');
            // No cambiar el estado a error, solo loguear como warning
            return;
          }
        }
        addLogEntry(`Error de Pusher: ${error.message || JSON.stringify(error)}`, 'error');
        setConnectionStatus('Error de conexión');
        setConnectionClass('disconnected');
      });

      // Agregar listener para cambios de estado de conexión
      pusher.connection.bind('state_change', (states) => {
        addLogEntry(`Cambio de estado Pusher: ${states.previous} → ${states.current}`, 'info');
      });

      // Agregar listener para errores de transporte
      pusher.connection.bind('transport_error', (error) => {
        if (error.error && error.error.message && error.error.message.includes('ERR_HTTP2_PING_FAILED')) {
          addLogEntry('Error de transporte HTTP/2 (normal, usando WebSocket)', 'warning');
          return;
        }
        addLogEntry(`Error de transporte: ${error.error?.message || JSON.stringify(error)}`, 'warning');
      });

      addLogEntry('Pusher JS inicializado correctamente (WebSocket only)', 'success');
      return pusher;

    } catch (error) {
      addLogEntry(`Error inicializando Pusher: ${error.message}`, 'error');
      isInitializedRef.current = false;
      return null;
    }
  }, [addLogEntry]);

  // Suscribirse a un canal de báscula específica (memoizada)
  const subscribeToScale = useMemo(() => (scaleId, callback) => {
    // Verificar si ya estamos suscritos al mismo canal
    if (channelRef.current && currentScaleIdRef.current === scaleId) {
      addLogEntry(`Ya estamos suscritos al canal scale-${scaleId}`, 'info');
      return channelRef.current;
    }

    if (!pusherRef.current) {
      addLogEntry('Pusher no está inicializado', 'error');
      console.error('Pusher no está inicializado');
      return null;
    }

    try {
      // Desuscribirse del canal anterior si existe
      if (channelRef.current) {
        addLogEntry(`🔄 Desuscribiéndose del canal anterior: scale-${currentScaleIdRef.current}`, 'info');
        channelRef.current.unsubscribe();
        channelRef.current = null;
      }

      addLogEntry(`🔍 Suscribiéndose al canal: scale-${scaleId}`, 'info');
      
      const channel = pusherRef.current.subscribe(`scale-${scaleId}`);
      channelRef.current = channel;
      currentScaleIdRef.current = scaleId;
      
      addLogEntry(`Canal suscrito exitosamente: scale-${scaleId}`, 'success');
      
      // Binding del evento new-weighing
      channel.bind('new-weighing', function(data) {
        try {
          // Validar que los datos estén presentes
          if (!data) {
            addLogEntry('Evento recibido sin datos', 'error');
            console.error('Evento new-weighing recibido sin datos:', data);
            return;
          }

          // Validar estructura de datos
          if (typeof data.weight === 'undefined' || typeof data.scaleId === 'undefined') {
            addLogEntry(`Datos incompletos recibidos: ${JSON.stringify(data)}`, 'error');
            console.error('Datos incompletos en evento new-weighing:', data);
            return;
          }

          // Normalizar tipos de datos
          const normalizedData = {
            weight: parseFloat(data.weight) || 0,
            scaleId: parseInt(data.scaleId) || 0
          };

          // Validar que los valores sean válidos
          if (normalizedData.scaleId <= 0) {
            addLogEntry(`scaleId inválido: ${normalizedData.scaleId}`, 'error');
            console.error('scaleId inválido:', normalizedData.scaleId);
            return;
          }

          if (normalizedData.weight < 0) {
            addLogEntry(`weight inválido: ${normalizedData.weight}`, 'error');
            console.error('weight inválido:', normalizedData.weight);
            return;
          }

          addLogEntry(`EVENTO new-weighing RECIBIDO: ${JSON.stringify(normalizedData)}`, 'event');
          
          // Ejecutar el callback con los datos normalizados
          if (typeof callback === 'function') {
            callback(normalizedData);
          } else {
            console.warn('Callback no es una función:', callback);
            addLogEntry('Callback no es una función', 'error');
          }
        } catch (error) {
          console.error('Error procesando evento new-weighing:', error);
          addLogEntry(`Error procesando evento: ${error.message}`, 'error');
        }
      });
      
      // Binding de otros eventos para debug
      channel.bind('pusher:subscription_succeeded', function() {
        addLogEntry(`Suscripción exitosa al canal scale-${scaleId}`, 'success');
      });
      
      channel.bind('pusher:subscription_error', function(status) {
        console.error(`Error en suscripción al canal scale-${scaleId}:`, status);
        addLogEntry(`Error en suscripción al canal scale-${scaleId}: ${JSON.stringify(status)}`, 'error');
      });
      
      return channel;
      
    } catch (error) {
      console.error(`Error suscribiéndose al canal:`, error);
      addLogEntry(`Error suscribiéndose al canal: ${error.message}`, 'error');
      return null;
    }
  }, [addLogEntry]);

  // Función para imprimir físicamente el peso (memoizada)
  const printWeight = useMemo(() => (weightData) => {
    try {
      // Crear contenido para imprimir
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Peso de Báscula</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
            .weight { font-size: 48px; font-weight: bold; color: #007bff; text-align: center; margin: 30px 0; }
            .details { margin: 20px 0; }
            .detail-row { display: flex; justify-content: space-between; margin: 10px 0; padding: 5px 0; border-bottom: 1px solid #eee; }
            .label { font-weight: bold; color: #555; }
            .value { color: #333; }
            .footer { text-align: center; margin-top: 30px; color: #666; font-size: 12px; }
            @media print { body { margin: 0; } .no-print { display: none; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>PESO DE BÁSCULA</h1>
            <h2>Zona Franca</h2>
          </div>
          
          <div class="weight">
            ${weightData.weight} kg
          </div>
          
          <div class="details">
            <div class="detail-row">
              <span class="label">Báscula ID:</span>
              <span class="value">${weightData.scaleId}</span>
            </div>
            <div class="detail-row">
              <span class="label">Fecha y Hora:</span>
              <span class="value">${new Date(weightData.timestamp).toLocaleString()}</span>
            </div>
            <div class="detail-row">
              <span class="label">Tipo:</span>
              <span class="value">Automático</span>
            </div>
            <div class="detail-row">
              <span class="label">Canal:</span>
              <span class="value">scale-${weightData.scaleId}</span>
            </div>
          </div>
          
          <div class="footer">
            <p>Impreso el: ${new Date().toLocaleString()}</p>
            <p>Sistema de Pesaje Automático</p>
          </div>
          
          <div class="no-print" style="text-align: center; margin-top: 20px;">
            <button onclick="window.print()" style="padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 5px; cursor: pointer;">
              Imprimir Peso
            </button>
            <button onclick="window.close()" style="padding: 10px 20px; background: #6c757d; color: white; border: none; border-radius: 5px; cursor: pointer; margin-left: 10px;">
              Cerrar
            </button>
          </div>
        </body>
        </html>
      `;

      // Abrir ventana de impresión
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Agregar entrada al log
      addLogEntry(`Ventana de impresión abierta para peso ${weightData.weight} kg`, 'info');
      
      // Mostrar notificación
      displayNotification('Ventana de impresión abierta', 3000);
      
    } catch (error) {
      console.error('Error al imprimir peso:', error);
      addLogEntry(`Error al imprimir: ${error.message}`, 'error');
      displayNotification('Error al imprimir peso', 3000);
    }
  }, [addLogEntry, displayNotification]);

  // Actualizar dashboard con nuevo peso (memoizada)
  const updateWeightDashboard = useMemo(() => (data) => {
    handleWeightData(data);
    
    if (!isMountedRef.current) {
      console.warn('⚠️ Componente no está montado, ignorando actualización');
      return;
    }

    // Actualizar estado local
    setCurrentWeight(data.weight);
    setCurrentScaleId(data.scaleId);
    setLastWeightTime(new Date());
          
    // Agregar entrada al log
    addLogEntry(`Peso recibido: ${data.weight} kg de báscula ${data.scaleId}`, 'success');
    
    // Imprimir automáticamente si está habilitado
    if (autoPrint) {
      const weightData = {
        weight: data.weight,
        scaleId: data.scaleId,
        timestamp: new Date().toISOString()
      };
      
      // Imprimir después de un pequeño delay para asegurar que el estado se actualice
      setTimeout(() => {
        printWeight(weightData);
      }, 500);
    } else {
      console.warn('Auto-impresión deshabilitada');
    }
    
    setLoading(false);
  }, [setLoading, addLogEntry, autoPrint, printWeight]);

  // Función para manejar datos de peso (memoizada)
  const handleWeightData = useMemo(() => (data) => {
    // Ejecutar función adicional si se proporciona
    if (executeFunction && typeof executeFunction === 'function') {
      try {
        executeFunction({
          weight: data.weight,
          scaleId: data.scaleId,
          timestamp: new Date().toISOString(),
          automatic: true,
          rawData: data
        });
      } catch (error) {
        console.error('Error ejecutando función adicional:', error);
      }
    } else {
      console.warn('No hay función adicional para ejecutar');
    }
  }, [executeFunction]);

  // Función para imprimir el peso actual (memoizada)
  const handlePrintCurrentWeight = useMemo(() => () => {
    if (currentWeight <= 0) {
      displayNotification('No hay peso para imprimir', 3000);
      return;
    }
    
    const weightData = {
      weight: currentWeight,
      scaleId: currentScaleId,
      timestamp: lastWeightTime ? lastWeightTime.toISOString() : new Date().toISOString()
    };
    
    printWeight(weightData);
  }, [currentWeight, currentScaleId, lastWeightTime, printWeight, displayNotification]);

  // Inicializar el sistema de eventos de peso (memoizada)
  const initWeightEvents = useMemo(() => (scaleId) => {
    addLogEntry(`Inicializando eventos de peso para báscula ${scaleId}`, 'info');
    
    // Suscribirse al canal de la báscula
    const channel = subscribeToScale(scaleId, function(data) {
      // Actualizar dashboard
      updateWeightDashboard(data);
    });
    
    if (channel) {
      setIsSubscribed(true);
      setCurrentScaleId(scaleId);
      addLogEntry(`Suscrito exitosamente a la báscula ${scaleId}`, 'success');
      displayNotification(`Suscrito a báscula ${scaleId}`, 3000);
      
      // Actualizar estado de conexión
      setConnectionStatus(`Conectado a báscula ${scaleId}`);
      setConnectionClass('connected');
    } else {
      console.error(`Canal inválido retornado por subscribeToScale`);
      addLogEntry(`Error al suscribirse a la báscula ${scaleId}`, 'error');
      displayNotification('Error al suscribirse', 3000);
    }
    
    return channel;
  }, [subscribeToScale, updateWeightDashboard, addLogEntry, displayNotification]);

  // Función principal para obtener el peso (memoizada)
  const getWeighing = useMemo(() => async () => {
    if (!isMountedRef.current) return;

    try {
      // Obtener ID de báscula del localStorage
      const scaleId = localStorage.getItem("chanelWeighing");
      
      if (!scaleId) {
        openAlert("Seleccione una báscula", "error");
        return;
      }

      const scaleIdNumber = parseInt(scaleId);
      if (isNaN(scaleIdNumber) || scaleIdNumber <= 0) {
        openAlert("ID de báscula inválido", "error");
        return;
      }

      setLoading(true);
      addLogEntry(`Iniciando conexión a báscula ${scaleIdNumber}`, 'info');

      // Inicializar eventos de peso
      const channel = initWeightEvents(scaleIdNumber);
      
      if (!channel) {
        setLoading(false);
        openAlert("Error al conectar con la báscula", "error");
      }

    } catch (error) {
      addLogEntry(`Error en getWeighing: ${error.message}`, 'error');
      openAlert("Error inesperado", "error");
      setLoading(false);
    }
  }, [setLoading, openAlert, initWeightEvents, addLogEntry]);

  // Limpiar recursos al desmontar
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (channelRef.current) {
        try {
          channelRef.current.unsubscribe();
          channelRef.current = null;
        } catch (error) {
          console.warn('Error al limpiar canal:', error);
        }
      }
      
      if (pusherRef.current) {
        try {
          pusherRef.current.disconnect();
          pusherRef.current = null;
          isInitializedRef.current = false;
        } catch (error) {
          console.warn('Error al desconectar Pusher:', error);
        }
      }
    };
  }, []);

  // Inicialización automática cuando se monta el componente (UNA SOLA VEZ)
  useEffect(() => {
    // Verificar si ya se inicializó
    if (isInitializedRef.current) {
      addLogEntry('Pusher ya está inicializado, saltando inicialización', 'info');
      return;
    }

    addLogEntry('Componente cargado, inicializando Pusher por primera vez', 'info');
    
    // Inicializar Pusher una sola vez
    const pusher = initPusher();
    
    if (pusher) {
      addLogEntry('WeightEvents cargado correctamente', 'success');
      
      // Obtener ID de báscula del localStorage
      const scaleId = localStorage.getItem("chanelWeighing");
      
      if (scaleId) {
        const scaleIdNumber = parseInt(scaleId);
        if (!isNaN(scaleIdNumber) && scaleIdNumber > 0) {
          addLogEntry(`🔄 Conectando automáticamente a báscula ${scaleIdNumber}`, 'info');
          // Conectar automáticamente a la báscula
          initWeightEvents(scaleIdNumber);
        }
      }
    } else {
      addLogEntry('WeightEvents no está disponible', 'error');
    }
  }, []); // Sin dependencias para ejecutar solo una vez

  // Maneja el click del botón (memoizada)
  const handleButtonClick = useMemo(() => () => {
    if (loading || isSubscribed) {
      return;
    }
    
    addLogEntry('Click en botón peso de báscula', 'info');
    getWeighing();
  }, [getWeighing, loading, isSubscribed, addLogEntry]);

  // Obtiene el texto del tooltip (memoizada)
  const getTooltipText = useMemo(() => {
    if (isSubscribed) {
      return `Conectado a báscula ${currentScaleId} - Esperando peso...`;
    }
    if (loading) {
      return 'Conectando a la báscula...';
    }
    return 'Obtener peso de la báscula';
  }, [isSubscribed, currentScaleId, loading]);

  // Obtiene el color del botón (memoizada)
  const getButtonColor = useMemo(() => {
    if (isSubscribed) {
      return '#4CAF50'; // Verde cuando está conectado
    }
    return undefined;
  }, [isSubscribed]);

  // Verifica si el botón debe estar deshabilitado (memoizada)
  const isButtonDisabled = useMemo(() => loading || isSubscribed, [loading, isSubscribed]);

  return (
    <>
      {loading && <SpinnerComponent size="auto" />}
      
      {/* Notificación de peso recibido */}
      {showNotificationState && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: '#28a745',
          color: 'white',
          padding: '15px',
          borderRadius: '5px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          zIndex: 1000,
          maxWidth: '300px',
          fontSize: '14px',
          fontWeight: 'bold'
        }}>
          {notificationMessage}
        </div>
      )}
      
      {!loading && (
        <Tooltip 
          placement="top" 
          hasArrow 
          label={
            <div style={{ maxWidth: '300px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                {getTooltipText}
              </div>
              
              {/* Información del peso actual */}
              {currentWeight > 0 && (
                <div style={{ fontSize: '12px', color: '#4CAF50', marginBottom: '5px', borderTop: '1px solid #555', paddingTop: '5px' }}>
                  <div>⚖️ Peso actual: {currentWeight} kg</div>
                  <div>🏭 Báscula: {currentScaleId}</div>
                  {lastWeightTime && (
                    <div>🕒 Último: {lastWeightTime.toLocaleTimeString()}</div>
                  )}
                  
                  {/* Botón de impresión */}
                  <div style={{ marginTop: '8px', textAlign: 'center' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrintCurrentWeight();
                      }}
                      style={{
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        padding: '4px 8px',
                        fontSize: '10px',
                        cursor: 'pointer',
                        width: '100%'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
                    >
                      🖨️ Imprimir Peso
                    </button>
                    
                    {/* Botón de prueba */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Simular recepción de peso
                        const testData = {
                          weight: 123.45,
                          scaleId: currentScaleId || 299,
                          timestamp: new Date().toISOString()
                        };
                        updateWeightDashboard(testData);
                      }}
                      style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        padding: '4px 8px',
                        fontSize: '10px',
                        cursor: 'pointer',
                        width: '100%',
                        marginTop: '5px'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#218838'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#28a745'}
                    >
                      🧪 Probar Flujo
                    </button>
                    
                    {/* Toggle para impresión automática */}
                    <div style={{ marginTop: '5px', fontSize: '9px', color: '#666' }}>
                      <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={autoPrint}
                          onChange={(e) => {
                            e.stopPropagation();
                            setAutoPrint(e.target.checked);
                          }}
                          style={{ marginRight: '4px', cursor: 'pointer' }}
                        />
                        🚀 Auto-imprimir
                      </label>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Estado de conexión */}
              <div style={{ fontSize: '11px', color: '#ccc', borderTop: '1px solid #555', paddingTop: '5px', marginTop: '5px' }}>
                <div>📡 Estado: {connectionStatus}</div>
                <div>🔑 Canal: {currentScaleId ? `scale-${currentScaleId}` : 'N/A'}</div>
                <div>🔄 Pusher: {window.Pusher ? '✅ Disponible' : '❌ No disponible'}</div>
                <div>🖨️ Auto-print: {autoPrint ? '✅ Activado' : '❌ Desactivado'}</div>
                <div>🔗 Suscrito: {isSubscribed ? '✅ Sí' : '❌ No'}</div>
                <div>📱 PusherRef: {pusherRef.current ? '✅ Inicializado' : '❌ No inicializado'}</div>
                <div>📺 ChannelRef: {channelRef.current ? '✅ Activo' : '❌ No activo'}</div>
                <div>🚀 Inicializado: {isInitializedRef.current ? '✅ Sí' : '❌ No'}</div>
              </div>
              
              {/* Log de eventos recientes */}
              <div style={{ fontSize: '10px', color: '#999', borderTop: '1px solid #555', paddingTop: '5px', marginTop: '5px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '3px' }}>📋 Log reciente:</div>
                {logEntries.slice(-3).map((entry) => (
                  <div key={entry.id} style={{ fontSize: '9px', marginBottom: '2px' }}>
                    [{entry.timestamp}] {entry.message.substring(0, 40)}...
                  </div>
                ))}
              </div>
            </div>
          }
        >
          <ButtonHeaderModule
            onClick={handleButtonClick}
            disabled={isButtonDisabled}
            bgColor={getButtonColor}
            className="mt-4"
            style={{
              opacity: isButtonDisabled ? 0.7 : 1,
              cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'none'
            }}
          >
            <IconGeneric 
              color="#ffff" 
              as={GiWeightScale} 
            />
          </ButtonHeaderModule>
        </Tooltip>
      )}
    </>
  );
};
